/**
 * euronics.ie — filter redesign behaviour
 * ───────────────────────────────────────
 * Progressive enhancement over the native Search & Discovery filter markup
 * rendered by main-collection.liquid / main-search.liquid. It does NOT replace
 * the theme's AJAX filtering — it drives the same underlying checkbox / price
 * inputs and lets the theme's facet form submit as normal.
 *
 * Adds, per filter group:
 *   • show-more for long checkbox lists (caps at 8)
 *   • search-within for big lists (Brand etc., >10 options)
 *   • colour swatches for the Colour group
 *   • a dual-handle range slider for numeric metafield groups
 *     (capacity / screen size / RAM …). Native S&D returns these as discrete
 *     value checkboxes, so the slider toggles the in-range checkboxes and lets
 *     the facet form apply them — no backend change.
 *   • a real dual-handle slider bound to the native price gte/lte inputs
 *
 * Re-runs after every AJAX filter re-render via a MutationObserver, because the
 * theme replaces the sidebar HTML on each apply.
 */
(function () {
  'use strict';

  var SHOW_LIMIT = 8;        // collapse checkbox lists longer than this
  var SEARCH_MIN = 8;        // add a search box to lists this long or longer
  var RANGE_MIN  = 6;        // only build a range slider when there are >= this many numeric values

  /* Canonical appliance colour → swatch fill. Unknown labels fall back to a
     neutral chip with the first letter, so nothing ever renders blank. */
  var COLOURS = {
    black: '#1c1c1c', white: '#ededed', silver: '#c2c6cc', grey: '#8d9298', gray: '#8d9298',
    'stainless steel': '#9ba0a6', inox: '#9ba0a6', 'brushed steel': '#a8acb1', steel: '#9ba0a6',
    graphite: '#3a4049', anthracite: '#33373d', 'space grey': '#4a4e54', 'space gray': '#4a4e54',
    red: '#c0392b', blue: '#2e5a88', navy: '#22314a', green: '#3b7a57', cream: '#efe7d6',
    beige: '#d8cdb4', gold: '#c8a96b', rose: '#d8a7a0', 'rose gold': '#d8a7a0', pink: '#d98fb0',
    purple: '#6c5cb9', yellow: '#e7c34a', orange: '#d2752f', brown: '#6f4e37', copper: '#b06a3b',
    titanium: '#8f9296', chrome: '#c9ced4', mirror: '#cfd4da', glass: '#dfe6ea'
  };

  function txt(el) { return (el && el.textContent || '').replace(/\s+/g, ' ').trim(); }
  function debounce(fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; }

  /* Fire one bubbling change so the theme's facet form serialises the whole
     form and re-renders. Used after we programmatically toggle inputs. */
  function applyVia(input) {
    if (!input) return;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function listInputs(group) {
    return Array.prototype.slice.call(group.querySelectorAll('.collection__filter-checkbox-list input[type="checkbox"]'));
  }

  /* ---- show-more --------------------------------------------------------- */
  function addShowMore(group, rows) {
    var list = group.querySelector('.collection__filter-checkbox-list');
    if (!list || rows.length <= SHOW_LIMIT) return;
    list.classList.add('fx-capped');
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fx-showmore';
    var hidden = rows.length - SHOW_LIMIT;
    btn.textContent = 'Show all ' + rows.length;
    btn.addEventListener('click', function () {
      var open = list.classList.toggle('fx-open');
      btn.textContent = open ? 'Show less' : ('Show all ' + rows.length);
    });
    list.parentNode.insertBefore(btn, list.nextSibling);
  }

  /* ---- search-within ----------------------------------------------------- */
  function addSearch(group, rows) {
    if (rows.length < SEARCH_MIN || group.querySelector('.fx-search')) return;
    var list = group.querySelector('.collection__filter-checkbox-list');
    if (!list) return;
    var wrap = document.createElement('div');
    wrap.className = 'fx-search';
    var input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Search ' + (txt(group.querySelector('.collection__filter-group-name')).toLowerCase() || 'options');
    input.setAttribute('aria-label', input.placeholder);
    wrap.appendChild(input);
    list.parentNode.insertBefore(wrap, list);
    input.addEventListener('input', function () {
      var q = input.value.toLowerCase();
      rows.forEach(function (li) { li.style.display = txt(li).toLowerCase().indexOf(q) > -1 ? '' : 'none'; });
    });
  }

  /* ---- colour swatches --------------------------------------------------- */
  function asSwatches(group) {
    var rows = Array.prototype.slice.call(group.querySelectorAll('.collection__filter-checkbox'));
    if (!rows.length) return;
    group.classList.add('fx-swatches');
    rows.forEach(function (li) {
      var cb = li.querySelector('input[type="checkbox"]');
      var label = li.querySelector('label');
      if (!cb || !label || li.querySelector('.fx-sw')) return;
      var name = txt(label).replace(/\(\d+\)\s*$/, '').trim();
      var key = name.toLowerCase();
      var hex = COLOURS[key];
      var sw = document.createElement('span');
      sw.className = 'fx-sw' + (cb.checked ? ' is-on' : '');
      sw.setAttribute('role', 'button');
      sw.setAttribute('tabindex', '0');
      sw.setAttribute('aria-label', name);
      sw.title = name;
      if (hex) { sw.style.background = hex; if (key === 'white' || key === 'cream' || key === 'glass') sw.style.borderColor = '#c4ccd8'; }
      else { sw.classList.add('fx-sw--text'); sw.textContent = name.charAt(0).toUpperCase(); }
      function toggle() { cb.checked = !cb.checked; sw.classList.toggle('is-on', cb.checked); applyVia(cb); }
      sw.addEventListener('click', toggle);
      sw.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
      li.appendChild(sw);
    });
  }

  /* ---- numeric range slider (toggles in-range checkboxes) ---------------- */
  function unitFromTitle(t) {
    var m = t.match(/\(([^)]+)\)/); // e.g. "Total Capacity (L)" → "L"
    if (m) return m[1];
    if (/\binches?\b|"/i.test(t)) return '"';
    return '';
  }

  function asRange(group) {
    var inputs = listInputs(group);
    var pure = /^\s*\d+(\.\d+)?\s*$/; // only treat genuinely numeric value lists as ranges ("4K"/"55 inch" stay lists)
    if (inputs.length < RANGE_MIN || !inputs.every(function (i) { return pure.test(i.value); })) return false;
    var nums = inputs.map(function (i) { return parseFloat(i.value); });

    /* sorted unique values + the checkbox for each */
    var byVal = {};
    inputs.forEach(function (cb, i) { byVal[nums[i]] = cb; });
    var vals = Object.keys(byVal).map(Number).sort(function (a, b) { return a - b; });
    if (vals.length < RANGE_MIN) return false;

    var title = txt(group.querySelector('.collection__filter-group-name'));
    var unit = unitFromTitle(title);
    group.classList.add('fx-range');

    /* current selection from already-checked boxes */
    var checked = vals.filter(function (v) { return byVal[v].checked; });
    var loIdx = checked.length ? vals.indexOf(checked[0]) : 0;
    var hiIdx = checked.length ? vals.indexOf(checked[checked.length - 1]) : vals.length - 1;

    var box = document.createElement('div');
    box.className = 'fx-rangebox';
    box.innerHTML =
      '<div class="fx-track"><div class="fx-fill"></div>' +
      '<input class="fx-lo" type="range" min="0" max="' + (vals.length - 1) + '" step="1" value="' + loIdx + '" aria-label="' + title + ' minimum">' +
      '<input class="fx-hi" type="range" min="0" max="' + (vals.length - 1) + '" step="1" value="' + hiIdx + '" aria-label="' + title + ' maximum"></div>' +
      '<div class="fx-readout"><span class="fx-min"></span><span class="fx-max"></span></div>';
    var list = group.querySelector('.collection__filter-collapsible') || group;
    list.appendChild(box);

    var lo = box.querySelector('.fx-lo'), hi = box.querySelector('.fx-hi'),
        fill = box.querySelector('.fx-fill'), outMin = box.querySelector('.fx-min'), outMax = box.querySelector('.fx-max');
    var fmt = function (v) { return Math.round(v) + (unit ? (unit === '"' ? '"' : ' ' + unit) : ''); };

    function paint() {
      var a = Math.min(+lo.value, +hi.value), b = Math.max(+lo.value, +hi.value);
      var pa = a / (vals.length - 1) * 100, pb = b / (vals.length - 1) * 100;
      fill.style.left = pa + '%'; fill.style.right = (100 - pb) + '%';
      outMin.textContent = fmt(vals[a]); outMax.textContent = fmt(vals[b]);
    }
    var apply = debounce(function () {
      var a = Math.min(+lo.value, +hi.value), b = Math.max(+lo.value, +hi.value);
      var lastChanged = null;
      vals.forEach(function (v, i) {
        var cb = byVal[v];
        var want = i >= a && i <= b && !(a === 0 && b === vals.length - 1); // full range = no filter
        if (cb.checked !== want) { cb.checked = want; lastChanged = cb; }
      });
      if (lastChanged) applyVia(lastChanged);
    }, 350);

    [lo, hi].forEach(function (s) {
      s.addEventListener('input', paint);
      s.addEventListener('change', apply);
    });
    paint();
    return true;
  }

  /* ---- price: replace Warehouse's chunky native slider with the SAME clean
     slider used for numeric metafields, driving the native inputs so the actual
     filtering stays native (zero apply risk). ---------------------------------- */
  function enhancePrice(group) {
    if (group.querySelector('.fx-rangebox')) return false;
    var nativeRanges = Array.prototype.slice.call(group.querySelectorAll('input[type="range"]'));
    if (nativeRanges.length < 2) return false;
    var nLo = nativeRanges[0], nHi = nativeRanges[1];
    var floor = parseFloat(nLo.min) || 0;
    var ceil = parseFloat(nLo.max) || parseFloat(nHi.max) || 0;
    if (ceil <= floor) return false;
    var nNums = Array.prototype.slice.call(group.querySelectorAll('.price-range__input input, .input-prefix__field'));
    group.classList.add('fx-priceslider'); // CSS hides the native .price-range markup

    var box = document.createElement('div');
    box.className = 'fx-rangebox';
    box.innerHTML =
      '<div class="fx-track"><div class="fx-fill"></div>' +
      '<input class="fx-lo" type="range" min="' + floor + '" max="' + ceil + '" step="10" value="' + (parseFloat(nLo.value) || floor) + '" aria-label="Minimum price">' +
      '<input class="fx-hi" type="range" min="' + floor + '" max="' + ceil + '" step="10" value="' + (parseFloat(nHi.value) || ceil) + '" aria-label="Maximum price"></div>' +
      '<div class="fx-readout"><span class="fx-min"></span><span class="fx-max"></span></div>';
    (group.querySelector('.collection__filter-collapsible') || group).appendChild(box);

    var lo = box.querySelector('.fx-lo'), hi = box.querySelector('.fx-hi'),
        fill = box.querySelector('.fx-fill'), oMin = box.querySelector('.fx-min'), oMax = box.querySelector('.fx-max');
    function paint() {
      var a = Math.min(+lo.value, +hi.value), b = Math.max(+lo.value, +hi.value);
      fill.style.left = (a - floor) / (ceil - floor) * 100 + '%';
      fill.style.right = (100 - (b - floor) / (ceil - floor) * 100) + '%';
      oMin.textContent = '€' + Math.round(a); oMax.textContent = '€' + Math.round(b);
    }
    var apply = debounce(function () {
      var a = Math.min(+lo.value, +hi.value), b = Math.max(+lo.value, +hi.value);
      nLo.value = a; nHi.value = b;
      if (nNums.length >= 2) { nNums[0].value = a > floor ? a : ''; nNums[1].value = b < ceil ? b : ''; }
      applyVia(nNums.length >= 2 ? nNums[1] : nHi);
    }, 350);
    [lo, hi].forEach(function (s) { s.addEventListener('input', paint); s.addEventListener('change', apply); });
    paint();
    return true;
  }

  /* ---- short-value pills (energy rating A–G, etc.) ----------------------- */
  function labelText(li) { return txt(li.querySelector('label')).replace(/\s*\(\d+\)\s*$/, '').trim(); }
  function asPills(group) {
    var rows = Array.prototype.slice.call(group.querySelectorAll('.collection__filter-checkbox'));
    if (rows.length < 2 || rows.length > 9) return false;
    if (!rows.every(function (li) { return labelText(li).length <= 3; })) return false;
    group.classList.add('fx-pills');
    rows.forEach(function (li) {
      var cb = li.querySelector('input[type="checkbox"]');
      if (!cb || li.querySelector('.fx-pill')) return;
      var pill = document.createElement('span');
      pill.className = 'fx-pill' + (cb.checked ? ' is-on' : '');
      pill.setAttribute('role', 'button');
      pill.setAttribute('tabindex', '0');
      pill.textContent = labelText(li);
      function toggle() { cb.checked = !cb.checked; pill.classList.toggle('is-on', cb.checked); applyVia(cb); }
      pill.addEventListener('click', toggle);
      pill.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
      li.appendChild(pill);
    });
    return true;
  }

  /* ---- per-group dispatcher --------------------------------------------- */
  function enhanceGroup(group) {
    if (group.dataset.fxDone) return;
    group.dataset.fxDone = '1';
    var title = txt(group.querySelector('.collection__filter-group-name')).toLowerCase();
    var rows = Array.prototype.slice.call(group.querySelectorAll('.collection__filter-checkbox'));

    if (/price/.test(title)) { enhancePrice(group); return; }
    if (/colou?r/.test(title) && rows.length) { asSwatches(group); addShowMore(group, rows); return; }
    if (rows.length && asRange(group)) { group.classList.add('fx-hidelist'); return; }
    if (rows.length && asPills(group)) return;
    if (rows.length) { addSearch(group, rows); addShowMore(group, rows); }
  }

  function initAll() {
    /* tag the "Filters" sidebar heading so the CSS header accent can target it */
    document.querySelectorAll('.layout__section--secondary .card__title--small, .card__title--small').forEach(function (el) {
      if (el.textContent.trim() === 'Filters') el.classList.add('fx-filters-heading');
    });
    document.querySelectorAll('.collection__filter-group').forEach(enhanceGroup);
  }

  /* re-run after AJAX filter re-renders (theme replaces sidebar HTML) */
  var reinit = debounce(initAll, 120);
  function observe() {
    var host = document.querySelector('.collection, main') || document.body;
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes.length) { reinit(); break; }
      }
    }).observe(host, { childList: true, subtree: true });
  }

  /* ---- list/grid view toggle fix --------------------------------------------
     The theme stores the layout in a cart attribute (collection_layout) and the
     native handler updates it WITHOUT re-rendering — so the toggle only took
     effect after a manual refresh, and the half-switched state rendered the wrong
     card format. Intercept the toggle (capture phase, before the native handler),
     persist the attribute, then reload so the server renders the correct layout
     (rich list card vs product grid) every time. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-action="change-layout"]');
    if (!btn) return;
    var mode = btn.getAttribute('data-layout-mode');
    if (!mode) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (btn.classList.contains('is-selected')) return; // already on this view
    btn.classList.add('fx-layout-loading');
    fetch('/cart/update.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { collection_layout: mode } })
    }).then(function () { window.location.reload(); })
      .catch(function () { window.location.reload(); });
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); observe(); });
  else { initAll(); observe(); }
})();
